import {FC, useMemo, useRef, useState} from "react";
import {Label, Stack, Text,} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import axios from "axios";
import {sanitize} from "dompurify";
import {CompanyEmailForward, CompanyEmailForwardContextEnum, DocumentType, PagedResult, THeMove,} from "../API";
import {TenantResource} from "../enums/TenantResource";
import {ITenantResourceQuery} from "../interfaces/ITenantResourceQuery";
import {
    CheckboxElement,
    CMEditorElement,
    CustomSearchBoxElement,
    EnumDropdownElement,
    GroupElement,
    ITenantFormLayout,
    MultilineTextElement,
    TextFieldElement,
} from "./OnixElements";
import {TenantList} from "./TenantList";
import {MakeTenantColumns} from "../helpers/MakeTenantColumns";
import {ITenantColumn} from "../components/OnixElements";
import {ShowApiError} from "./ShowApiError";
import {AxiosLoader} from "./AxiosLoader";
import Loading from "./Loading";

interface ICompanyForwardEmailsSectionProps {
    companyId: number;
    documentTypeId?: number;
}

export const CompanyForwardEmailsSection: FC<
    ICompanyForwardEmailsSectionProps
> = ({companyId, documentTypeId}) => {
    const {t} = useTranslation();
    const [focused, setFocused] = useState<CompanyEmailForward>();
    const reloadListRef = useRef<
        (() => Promise<PagedResult<CompanyEmailForward>>) | undefined
    >();
    const [testGeneratedDocument, setTestGeneratedDocument] = useState<
        | {
        title: string;
        body: string;
        fileName: string;
    }
        | undefined
    >();

    const companyEmailForwardQuery = useMemo<
        ITenantResourceQuery<CompanyEmailForward>
    >(() => {
        if (documentTypeId) {
            return {
                companySettingsId: {Equals: companyId},
                documentTypeId: {Equals: documentTypeId},
            };
        } else {
            return {
                companySettingsId: {Equals: companyId},
                // documentTypeId: { IsNull: true },
            };
        }
    }, [companyId, documentTypeId]);

    const companyEmailForwardBaseDto = useMemo<
        Partial<CompanyEmailForward>
    >(() => {
        if (documentTypeId) {
            return {companySettingsId: companyId, documentTypeId: documentTypeId};
        } else {
            return {companySettingsId: companyId};
        }
    }, [companyId, documentTypeId]);

    const inlineFocusedForm = useMemo<ITenantFormLayout<CompanyEmailForward>>(
        () => ({
            context: {
                type: new EnumDropdownElement({
                    enumData: CompanyEmailForwardContextEnum,
                }),
            },
            receivingEmail: {
                type: new TextFieldElement({}),
            },
            cc: {
                type: new TextFieldElement({}),
            },
            bcc: {
                type: new TextFieldElement({}),
            },
            documentTypeId: {
                type: new CustomSearchBoxElement<DocumentType, CompanyEmailForward>({
                    customSearchBoxType: "DEFAULT_CRUD",
                    resource: TenantResource.AdminDocumentTypes,
                    key: "id",
                    subtitleKey: "id",
                    titleKey: "type",
                    query: {
                        companySettingsId: {
                            Equals: companyId,
                        },
                    },
                }),
                disabledIf: (dto) => documentTypeId !== undefined,
                hiddenIf: (dto) => documentTypeId !== undefined,
            },
            titleFormat: {
                type: new TextFieldElement({}),
            },
            titleParams: {
                type: new TextFieldElement({}),
            },
            bodyFormat: {
                type: new MultilineTextElement({
                    rows: 10,
                }),
            },
            bodyParams: {
                type: new TextFieldElement({}),
            },
            isBodyHtml: {
                type: new CheckboxElement({}),
            },
            documentTypeParams: {
                type: new TextFieldElement({}),
            },
            documentPicker: {
                type: new GroupElement<CompanyEmailForward>({
                    tHeMove: {
                        type: new CustomSearchBoxElement<THeMove, CompanyEmailForward>({
                            customSearchBoxType: "DEFAULT_CRUD",
                            key: "acKey",
                            titleKey: "acKey",
                            subtitleKey: "acReceiver",
                            resource: TenantResource.AdminTHeMove,
                            orderBy: "acKey",
                            orderByDescending: true,
                            additionalIntParameter: (dto) => companyId,
                        }),
                    },
                    tHeOrder: {
                        type: new CustomSearchBoxElement({
                            customSearchBoxType: "DEFAULT_CRUD",
                            key: "acKey",
                            titleKey: "acKey",
                            subtitleKey: "acReceiver",
                            resource: TenantResource.AdminTHeOrder,
                            orderBy: "acKey",
                            orderByDescending: true,
                            additionalIntParameter: (dto) => companyId,
                        }),
                    },
                }),
            },
            documentFilterSQL: {
                type: new CMEditorElement({
                    codeLanguage: "sql",
                }),
            },
            documentItemsFilterSQL: {
                type: new CMEditorElement({
                    codeLanguage: "sql",
                }),
            },
        }),
        [companyId, documentTypeId],
    );

    const companyEmailForwardColumns = useMemo<
        ITenantColumn<CompanyEmailForward>[]
    >(
        () =>
            MakeTenantColumns<CompanyEmailForward>(
                {
                    context: {
                        render: (item: CompanyEmailForward) => (
                            <span>
                {item.context != null && item.context != undefined
                    ? CompanyEmailForwardContextEnum[item.context]
                    : ""}
              </span>
                        ),
                    },
                    receivingEmail: {},
                    cc: {},
                    bcc: {},
                    documentTypeId: {},
                },
                t,
            ),
        [t],
    );

    return (
        <Stack>
            <Label>
                U naslov se može napisati npr. {"{acDoc1}"} tako će na to mjesto doći
                acDoc1 iz the_order ili the_move napravljenog dokumenta, ovisno o
                kontekstu isto i za Sadržaj.
            </Label>
            <Label>
                U sadržaj se još može napisati {"{RecapTable}"} tako će na to mjesto
                doći recap tablice/stavaka iz the_order ili the_move napravljenog
                dokumenta, ovisno o kontekstu.
            </Label>
            <TenantList<CompanyEmailForward, CompanyEmailForward, CompanyEmailForward>
                resource={TenantResource.AdminCompanyEmailForwards}
                baseDto={companyEmailForwardBaseDto}
                columns={companyEmailForwardColumns}
                form={inlineFocusedForm}
                query={companyEmailForwardQuery}
                queryIsRequired
                stretchForm={false}
                title="forwarding_emails"
                addingTitle="add_forwarding_email"
                editingTitle="edit_forwarding_email"
                disableWrappingOnChildren
                formChildren={(dto: any) => (
                    <div style={{width: "100%"}}>
                        {(dto?.["tHeMove"]?.trim() ?? dto?.["tHeOrder"]?.trim()) ? (
                            <AxiosLoader<{ title: string; body: string; fileName: string }>
                                method={axios.post}
                                debounce={1000}
                                url={`/AdminCompanyEmailForwards/TestGenerateDocument?acKey=${
                                    dto?.["tHeMove"] ?? dto?.["tHeOrder"]
                                }`}
                                config={dto}
                            >
                                {(testGeneratedDocument, error) =>
                                    !testGeneratedDocument && !error ? (
                                        <Loading/>
                                    ) : error ? (
                                        <ShowApiError errors={error}/>
                                    ) : (
                                        <Stack>
                                            <Label>
                                                Naslov <Text>{testGeneratedDocument!.title}</Text>
                                            </Label>
                                            <Label>
                                                Datoteka <Text>{testGeneratedDocument!.fileName}</Text>
                                            </Label>
                                            <Label>Sadržaj</Label>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: sanitize(testGeneratedDocument?.body ?? ""),
                                                }}
                                            />
                                        </Stack>
                                    )
                                }
                            </AxiosLoader>
                        ) : (
                            <label>Odaberite tHeMove ili tHeOrder dokument za prikaz</label>
                        )}
                    </div>
                )}
            />
        </Stack>
    );
};
